import React from 'react';
import {Stack, Typography} from '@mui/material';
import {t} from 'i18next';
import './QuerySubheader.scss';
import ArrButton from '../../../common/arr-button/ArrButton';
import {SessionSearch} from '../../types/SessionSearch';
import {Query, QueryType} from '../../types/Query';

export interface QuerySubheaderProps {
  item: SessionSearch;
  onAddContentAndMetadataQuery?: (item: SessionSearch) => void;
  onAddConceptualQuery?: (item: SessionSearch) => void;
  onAddWorkProductQuery?: (item: SessionSearch) => void;
  onAddAnalyticsQuery?: (item: SessionSearch) => void;
}

const QuerySubheader: React.FC<QuerySubheaderProps> = ({
  item,
  onAddContentAndMetadataQuery,
  onAddConceptualQuery,
  onAddWorkProductQuery,
  onAddAnalyticsQuery,
}) => {
  const appendToQuery = (query: Query) => {
    const itemCopy = JSON.parse(JSON.stringify(item));
    if (itemCopy.query) {
      itemCopy.query.push(query);
    } else {
      itemCopy.query = [query];
    }

    return itemCopy;
  };

  const createContentAndMetadataQuery = () => {
    const query: Query = {
      type: QueryType.ContentAndMetadata,
      value: [],
      operator: 'AND',
    };
    return query;
  };

  const hasContentAndMetadataQuery = () => {
    // Can only add one of each query type
    // If this query type already exists then we can't add more
    return item.query?.some((query) => query.type === 'ContentAndMetadata');
  };

  const handleAddContentAndMetadataButtonClick = (
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation();
    const query = createContentAndMetadataQuery();
    const updatedItem = appendToQuery(query);
    onAddContentAndMetadataQuery && onAddContentAndMetadataQuery(updatedItem);
  };

  const handleAddContentAndMetadataButtonKeyDown = (
    e: React.KeyboardEvent<HTMLElement>
  ) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      const query = createContentAndMetadataQuery();
      const updatedItem = appendToQuery(query);
      onAddContentAndMetadataQuery && onAddContentAndMetadataQuery(updatedItem);
    }
  };

  const createConceptualQuery = () => {
    const query: Query = {
      type: QueryType.Conceptual,
      value: [],
      operator: 'AND',
    };
    return query;
  };

  const hasConceptualQuery = () => {
    // Can only add one of each query type
    // If this query type already exists then we can't add more
    return item.query?.some((query) => query.type === 'Conceptual');
  };

  const handleAddConceptualButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const query = createConceptualQuery();
    const updatedItem = appendToQuery(query);
    onAddConceptualQuery && onAddConceptualQuery(updatedItem);
  };

  const handleAddConceptualButtonKeyDown = (
    e: React.KeyboardEvent<HTMLElement>
  ) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      const query = createConceptualQuery();
      const updatedItem = appendToQuery(query);
      onAddConceptualQuery && onAddConceptualQuery(updatedItem);
    }
  };

  const createWorkProductQuery = () => {
    const query: Query = {
      type: QueryType.WorkProduct,
      value: [],
      operator: 'AND',
    };
    return query;
  };

  const hasWorkProductQuery = () => {
    // Can only add one of each query type
    // If this query type already exists then we can't add more
    return item.query?.some((query) => query.type === 'WorkProduct');
  };

  const handleAddWorkProductButtonClick = (
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation();
    const query = createWorkProductQuery();
    const updatedItem = appendToQuery(query);
    onAddWorkProductQuery && onAddWorkProductQuery(updatedItem);
  };

  const handleAddWorkProductButtonKeyDown = (
    e: React.KeyboardEvent<HTMLElement>
  ) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      const query = createWorkProductQuery();
      const updatedItem = appendToQuery(query);
      onAddWorkProductQuery && onAddWorkProductQuery(updatedItem);
    }
  };

  const createAnalyticsQuery = () => {
    const query: Query = {
      type: QueryType.Analytics,
      value: [],
      operator: 'AND',
    };
    return query;
  };

  const hasAnalyticsQuery = () => {
    // Can only add one of each query type
    // If this query type already exists then we can't add more
    return item.query?.some((query) => query.type === 'Analytics');
  };

  const handleAddAnalyticsButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const query = createAnalyticsQuery();
    const updatedItem = appendToQuery(query);
    onAddAnalyticsQuery && onAddAnalyticsQuery(updatedItem);
  };

  const handleAddAnalyticsButtonKeyDown = (
    e: React.KeyboardEvent<HTMLElement>
  ) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      const query = createAnalyticsQuery();
      const updatedItem = appendToQuery(query);
      onAddAnalyticsQuery && onAddAnalyticsQuery(updatedItem);
    }
  };

  return (
    <Stack
      direction={'row'}
      spacing={1}
      className="session-search-query-subheader"
    >
      <Typography
        className="typo-text buttonSmall_size"
        data-testid={`query-subheader-add-label`}
        tabIndex={0}
      >
        {t('sessionSearch.querySubheader.addLabel')}
      </Typography>
      <ArrButton
        size="medium"
        variant="secondary"
        isAriaDisabled={hasContentAndMetadataQuery()}
        onClick={handleAddContentAndMetadataButtonClick}
        onKeyDown={handleAddContentAndMetadataButtonKeyDown}
        className={`button-add-content_and_metadata ${hasContentAndMetadataQuery() ? 'btn-disabled' : ''}`}
        dataTestId="button-add-content_and_metadata"
      >
        {t('sessionSearch.querySubheader.contentAndMetadataButton')}
      </ArrButton>
      <ArrButton
        size="medium"
        variant="secondary"
        isAriaDisabled={hasConceptualQuery()}
        onClick={handleAddConceptualButtonClick}
        onKeyDown={handleAddConceptualButtonKeyDown}
        className={`button-add-conceptual ${hasConceptualQuery() ? 'btn-disabled' : ''}`}
        dataTestId="button-add-conceptual"
      >
        {t('sessionSearch.querySubheader.conceptualButton')}
      </ArrButton>
      <ArrButton
        size="medium"
        variant="secondary"
        isAriaDisabled={hasWorkProductQuery()}
        onClick={handleAddWorkProductButtonClick}
        onKeyDown={handleAddWorkProductButtonKeyDown}
        className={`button-add-work_product ${hasWorkProductQuery() ? 'btn-disabled' : ''}`}
        dataTestId="button-add-work_product"
      >
        {t('sessionSearch.querySubheader.workProductButton')}
      </ArrButton>
      <ArrButton
        size="medium"
        variant="secondary"
        isAriaDisabled={hasAnalyticsQuery()}
        onClick={handleAddAnalyticsButtonClick}
        onKeyDown={handleAddAnalyticsButtonKeyDown}
        className={`button-add-analytics ${hasAnalyticsQuery() ? 'btn-disabled' : ''}`}
        dataTestId="button-add-analytics"
      >
        {t('sessionSearch.querySubheader.analyticsButton')}
      </ArrButton>
    </Stack>
  );
};

export default QuerySubheader;
